import { uuidv7 } from '../utils/uuid';
import type { StorageAdapter } from './storage';

const ANONYMOUS_ID_KEY = 'anonymous_user_id';
const END_USER_ID_KEY = 'end_user_id';
const SESSION_ID_KEY = 'session_id';
const SESSION_LAST_ACTIVE_KEY = 'session_last_active';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutos

/**
 * Gestiona la identidad triple del SDK:
 * - `anonymousId`: persiste indefinidamente (≥ 1 año).
 * - `endUserId`: presente tras `identify()`.
 * - `sessionId`: se renueva tras 30 min de inactividad.
 */
export class Session {
  private _anonymousId: string;
  private _sessionId: string;
  private _endUserId: string | null = null;

  constructor(private readonly storage: StorageAdapter) {
    this._anonymousId = this.loadOrCreateAnonymousId();
    this._sessionId = this.loadOrCreateSession();
    // Restaurar la identidad persistida: sin esto el SDK "olvida" al usuario
    // en cada recarga y los eventos posteriores caen al anonymous_id hasta
    // que la app vuelva a llamar identify(). Persistimos end_user_id en
    // setEndUserId(), así que también hay que rehidratarlo aquí.
    this._endUserId = this.storage.get(END_USER_ID_KEY);
  }

  get anonymousId(): string {
    return this._anonymousId;
  }

  get sessionId(): string {
    return this._sessionId;
  }

  get endUserId(): string | null {
    return this._endUserId;
  }

  /** ID a usar como visitor en eventos (end_user_id si está identificado, si no anonymous). */
  get effectiveUserId(): string {
    return this._endUserId ?? this._anonymousId;
  }

  setEndUserId(id: string): void {
    this._endUserId = id;
    this.storage.set(END_USER_ID_KEY, id);
  }

  /** Renueva el timestamp de actividad. Si pasaron > 30 min, genera nueva sesión. */
  touch(): void {
    const lastActive = this.storage.get(SESSION_LAST_ACTIVE_KEY);
    const now = Date.now();

    if (lastActive && now - Number.parseInt(lastActive, 10) > SESSION_TIMEOUT_MS) {
      this._sessionId = uuidv7();
      this.storage.set(SESSION_ID_KEY, this._sessionId);
    }

    this.storage.set(SESSION_LAST_ACTIVE_KEY, String(now));
  }

  /** Limpia la identidad (reset) pero conserva el anonymous_id. */
  reset(): void {
    this._endUserId = null;
    this.storage.remove(END_USER_ID_KEY);
    // Regenerar sesión.
    this._sessionId = uuidv7();
    this.storage.set(SESSION_ID_KEY, this._sessionId);
    this.storage.set(SESSION_LAST_ACTIVE_KEY, String(Date.now()));
  }

  private loadOrCreateAnonymousId(): string {
    let id = this.storage.get(ANONYMOUS_ID_KEY);
    if (!id) {
      id = uuidv7();
      this.storage.set(ANONYMOUS_ID_KEY, id);
    }
    return id;
  }

  private loadOrCreateSession(): string {
    const existing = this.storage.get(SESSION_ID_KEY);
    const lastActive = this.storage.get(SESSION_LAST_ACTIVE_KEY);

    if (existing && lastActive) {
      const elapsed = Date.now() - Number.parseInt(lastActive, 10);
      if (elapsed < SESSION_TIMEOUT_MS) {
        return existing;
      }
    }

    const newId = uuidv7();
    this.storage.set(SESSION_ID_KEY, newId);
    this.storage.set(SESSION_LAST_ACTIVE_KEY, String(Date.now()));
    return newId;
  }
}
